import React, { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';

/**
 * Diálogo de escaneo QR/Barcode.
 *
 * Encapsula todo el ciclo de vida de `Html5Qrcode`:
 * - Al abrirse, inicia la cámara trasera y comienza a escanear.
 * - Al detectar un código, llama `onScanSuccess(decodedText)` y se cierra automáticamente.
 * - Al cerrarse (por cualquier vía), detiene y limpia el scanner.
 *
 * @param {boolean} open
 * @param {function} onOpenChange  (open: boolean) => void
 * @param {function} onScanSuccess  (decodedText: string) => void
 */
export function QRScannerDialog({ open, onOpenChange, onScanSuccess }) {
  const scannerRef = useRef(null);
  const elementId = 'qr-reader-dialog';

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const startScanner = async () => {
      try {
        const scanner = new Html5Qrcode(elementId);
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            if (cancelled) return;
            onScanSuccess(decodedText);
            toast.success('Código escaneado correctamente', {
              description: decodedText.substring(0, 50) + (decodedText.length > 50 ? '...' : '')
            });
            onOpenChange(false);
          },
          () => {
            // Error callback continuo (QR no detectado en cada frame) — ignorar.
          }
        );
      } catch (err) {
        if (!cancelled) {
          toast.error('No se pudo acceder a la cámara');
          onOpenChange(false);
        }
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      const scanner = scannerRef.current;
      if (scanner) {
        scanner.stop().catch(() => {
          // Silent cleanup
        }).finally(() => {
          try {
            scanner.clear();
          } catch (_e) {
            // Already cleared
          }
          scannerRef.current = null;
        });
      }
    };
  }, [open, onScanSuccess, onOpenChange]);

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ maxWidth: '500px' }} data-testid="qr-scanner-dialog">
        <DialogHeader>
          <DialogTitle>Escanear Código QR/Barcode</DialogTitle>
          <DialogDescription>
            Apunta la cámara hacia el código del dispositivo
          </DialogDescription>
        </DialogHeader>
        <div style={{ padding: '20px' }}>
          <div
            id={elementId}
            style={{
              width: '100%',
              border: '2px solid #2d8a4e',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          />
          <Button
            type="button"
            variant="outline"
            data-testid="qr-scanner-cancel-button"
            onClick={handleCancel}
            style={{ marginTop: '16px', width: '100%' }}
          >
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default QRScannerDialog;
